import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ColumnMappings,
  IUploaderColumnConfig,
  UploaderSubmissionData,
  ValidationResult,
  ValidationWarning,
  ValidationWarningCode,
  ValidationErrorCode
} from "../../types/types";
import I18n from "../../../../../core/localization/I18n";
import "./_validate-data.scss";
import "./_validation-grid.scss";
import { Button, ButtonTypes } from "../../../Button";
import { AppGrid } from "../../../../../core/grids/builder/directorBuilder/grids/base/AppGrid_view";
import { mapFieldsToColDefs } from "../../utils/agGridColDefMapper/mapFieldsToColDefs";
import { UiSizes } from "@flightpath/coreui/dist/enums";
import { PositionedSpinner } from "../../../PositionedSpinner";

interface ValidateDataViewProps {
  data: any[];
  columns: ColumnMappings;
  headerValues: string[];
  fields: IUploaderColumnConfig[];
  entityRelations: Record<string, any[]>;
  onValidate: (data: any[]) => Promise<ValidationResult>;
  onCellEdit: (rowIndex: number, fieldKey: string, newValue: any) => Promise<void>;
  onRowDelete: (rowIndex: number) => Promise<void>;
  onAddRow: () => Promise<void>;
  onSubmit: (data: UploaderSubmissionData<any>) => Promise<void>;
  onBack: () => void;
  rowHook?: (row: any, addError: (fieldKey: string, error: any) => void, table: any[]) => any | Promise<any>;
  tableHook?: (
    table: any[],
    addError: (rowIndex: number, fieldKey: string, error: any) => void
  ) => any[] | Promise<any[]>;
  onLoadExistingData: () => Promise<{ existingData: any[]; relationData: Record<string, any[]> }>;
  existingData: any[];
  conflictFields?: string[]; // Add conflict fields prop
}

export const ValidateDataView: React.FC<ValidateDataViewProps> = ({
  data,
  columns,
  headerValues,
  fields,
  entityRelations,
  onValidate,
  onCellEdit,
  onRowDelete,
  onAddRow,
  onSubmit,
  onBack,
  rowHook,
  tableHook,
  onLoadExistingData,
  existingData,
  conflictFields = []
}) => {
  const gridRef = useRef<AppGrid>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedData, setEditedData] = useState(
    data.map((e, idx) => {
      return { ...e, id: idx + 1 };
    })
  );
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [loadedData, setLoadedData] = useState<any[]>(existingData);
  const [relationData, setRelationData] = useState<Record<string, any[]>>({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [conflictRows, setConflictRows] = useState<Set<number>>(new Set());
  const [newRows, setNewRows] = useState<Set<number>>(new Set());
  const [conflictResolution] = useState<"keep" | "replace" | null>(null);
  const [replacedRows, setReplacedRows] = useState<Map<number, number>>(new Map());
  const [deletedExistingIds, setDeletedExistingIds] = useState<Set<number>>(new Set());
  const [keptExistingRows, setKeptExistingRows] = useState<Set<number>>(new Set());

  //#region Computed Values [#00012350]
  const displayData = useMemo(() => {
    if (showErrorsOnly) {
      return editedData.filter((_, index) => {
        return validationResult?.errors.filter(vr => vr.rowId === _.id).length > 0;
      });
    }
    return editedData;
  }, [editedData, showErrorsOnly, validationResult]);

  const conflictWarnings = useMemo(() => {
    return validationResult?.warnings.filter(e => e.code === ValidationWarningCode.CONFLICT_WITH_EXISTING);
  }, [validationResult]);

  const duplicatesWarnings = useMemo(() => {
    return validationResult?.warnings.filter(e => e.code === ValidationWarningCode.DUPLICATE_VALUE);
  }, [validationResult]);

  const editedDataWithDuplicateRows = useMemo(() => {
    return editedData.filter(
      row =>
        validationResult?.warnings.filter(
          vw => vw.rowId === row.id && vw.code === ValidationWarningCode.DUPLICATE_VALUE
        ).length > 0
    );
  }, [editedData, validationResult]);

  // Helper function to check if there are any email validation errors
  const hasEmailValidationErrors = useMemo(() => {
    if (!validationResult) return false;
    const existingRowIds = new Set(editedData.map(row => row.id));
    const emailErrors = validationResult.errors.filter(
      error =>
        error.rowId !== undefined && existingRowIds.has(error.rowId) && error.code === ValidationErrorCode.INVALID_EMAIL
    );
    return emailErrors.length > 0;
  }, [validationResult, editedData]);

  const editedDataErrorRows = useMemo(() => {
    return editedData.filter(row => validationResult?.errors.filter(vr => vr.rowId === row.id)?.length > 0);
  }, [editedData, validationResult]);

  const columnDefs = useMemo(() => {
    // Merge entityRelations from props with relationData from addons
    const mergedEntityRelations = { ...entityRelations, ...relationData };

    return mapFieldsToColDefs(fields, {
      entityRelations: mergedEntityRelations,
      columns,
      editable: true,
      validationResult: {
        conflicts: conflictWarnings,
        errors: validationResult?.errors,
        duplicates: duplicatesWarnings
      },
      onCellValueChanged: params => {
        handleCellChange(params.node.rowIndex, params.colDef.field, params.newValue);
      }
    });
    // eslint-disable-next-line
  }, [fields, entityRelations, relationData, columns, validationResult, editedData]);

  //#endregion

  useEffect(() => {
    loadExistingData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (loadedData.length > 0 && editedData.length > 0) {
      handleValidation();
    }
    // eslint-disable-next-line
  }, [loadedData]);

  useEffect(() => {
    setEditedData(
      data.map((e, idx) => {
        return { ...e, id: idx };
      })
    );
  }, [data]);

  useEffect(() => {
    handleValidation();
    // eslint-disable-next-line
  }, [editedData]);

  // default value matching when relationData
  useEffect(() => {
    if (relationData && Object.keys(relationData).length > 0 && editedData.length > 0) {
      const dataWithDefaults = applyDefaultValueMatching(editedData, relationData);

      if (JSON.stringify(dataWithDefaults) !== JSON.stringify(editedData)) {
        setEditedData(dataWithDefaults);
      }
    }
    // eslint-disable-next-line
  }, [relationData]);

  /**
   * Helper function to find entity ID by matching names
   */
  const findEntityIdByName = (searchValue: string, entities: any[]): number | null => {
    if (!searchValue || !entities?.length) return null;

    const normalizedSearchValue = searchValue.toString().toLowerCase().trim();

    for (const entity of entities) {
      if (entity.name && entity.name.toLowerCase().trim() === normalizedSearchValue) {
        return entity.id;
      }

      if (entity.firstName && entity.lastName) {
        const fullName = `${entity.firstName} ${entity.lastName}`.toLowerCase().trim();
        if (fullName === normalizedSearchValue) {
          return entity.id;
        }
      }
    }

    return null;
  };

  const applyDefaultValueMatching = (data: any[], relationData: Record<string, any[]>): any[] => {
    if (!relationData || Object.keys(relationData).length === 0) {
      return data;
    }

    const entityRelationFields = fields.filter(field => field.type === "entityRelation");

    if (entityRelationFields.length === 0) {
      return data;
    }

    return data.map((row, index) => {
      const updatedRow = { ...row };
      let hasChanges = false;

      entityRelationFields.forEach(field => {
        const fieldKey = field.key;
        const currentValue = row[fieldKey];

        if (typeof currentValue === "number" && currentValue > 0) {
          return;
        }

        const entities = relationData[fieldKey] || [];
        if (entities.length === 0) {
          return;
        }

        const matchedEntityId = findEntityIdByName(currentValue, entities);

        if (matchedEntityId) {
          updatedRow[fieldKey] = matchedEntityId;
          hasChanges = true;
        } else {
          updatedRow[fieldKey] = null;
        }
      });

      if (hasChanges) {
        updatedRow._modified = true;
      }

      return updatedRow;
    });
  };

  const loadExistingData = async () => {
    setIsLoadingData(true);
    try {
      const result = await onLoadExistingData();

      setLoadedData(result.existingData);
      setRelationData(result.relationData);

      const dataWithDefaults = applyDefaultValueMatching(editedData, result.relationData);

      if (JSON.stringify(dataWithDefaults) !== JSON.stringify(editedData)) {
        setEditedData(dataWithDefaults);
      }

      identifyConflictsAndNewRows(dataWithDefaults);
    } catch (error) {
      console.error("Error loading existing data:", error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const identifyConflictsAndNewRows = (dataToCheck: any[]) => {
    const conflicts = new Set<number>();
    const newItems = new Set<number>();

    dataToCheck.forEach(row => {
      let isNew = true;
      let hasConflict = false;

      // Only check conflict fields for matches
      if (conflictFields.length > 0) {
        for (const conflictField of conflictFields) {
          if (row[conflictField]) {
            const matchingExistingRow = loadedData.find(
              existing => existing[conflictField] && existing[conflictField] === row[conflictField]
            );

            if (matchingExistingRow) {
              isNew = false;
              // Mark as conflict if not already marked as update action
              if (!row._action || row._action !== "update") {
                hasConflict = true;
              }
              break;
            }
          }
        }
      }

      if (hasConflict) conflicts.add(row.id);
      if (isNew) newItems.add(row.id);
    });

    setConflictRows(conflicts);
    setNewRows(newItems);

    return { conflicts, newItems };
  };

  const handleResolveAllConflicts = (resolution: "keep" | "replace") => {
    if (resolution === "keep") {
      const keptRows = new Set<number>();
      editedData.forEach(row => {
        if (conflictRows.has(row.id)) {
          keptRows.add(row.id);
        }
      });
      setKeptExistingRows(keptRows);
    } else if (resolution === "replace") {
      const newReplacedRows = new Map();
      const primaryKeyField = fields.find(f => f.required)?.key || "name";

      const updatedData = editedData.map(row => {
        if (conflictRows.has(row.id)) {
          let existingRow = loadedData.find(existing => existing[primaryKeyField] === row[primaryKeyField]);

          if (!existingRow && conflictFields.length > 0) {
            for (const conflictField of conflictFields) {
              if (row[conflictField]) {
                existingRow = loadedData.find(
                  existing => existing[conflictField] && existing[conflictField] === row[conflictField]
                );
                if (existingRow) break;
              }
            }
          }

          if (existingRow && existingRow.id) {
            newReplacedRows.set(row.id, existingRow.id);
            return { ...row, _action: "update" };
          }
        }
        return row;
      });

      setEditedData(updatedData);
      setReplacedRows(newReplacedRows);
      setKeptExistingRows(new Set());

      setTimeout(() => {
        trackPotentialDeletions();
      }, 0);
    }

    handleValidation();
  };

  const handleDeleteErrorRows = () => {
    const remainingData = editedData.filter(row => {
      return !validationResult?.errors.filter(vr => vr.rowId === row.id).length;
    });

    setEditedData(remainingData);
    identifyConflictsAndNewRows(remainingData);
    handleValidation();
  };

  const handleClearDuplicateRows = () => {
    const remainingData = editedData.filter(row => {
      return !validationResult?.warnings.filter(
        vr => vr.rowId === row.id && vr.code === ValidationWarningCode.DUPLICATE_VALUE
      ).length;
    });

    setEditedData(remainingData);
    identifyConflictsAndNewRows(remainingData);
    handleValidation();
  };

  const handleValidation = async () => {
    setIsValidating(true);
    try {
      let conflicts = new Set<number>();
      if (!conflictResolution) {
        conflicts = identifyConflictsAndNewRows(editedData).conflicts;
      }

      const result = await onValidate(editedData);

      const conflictWarningsNew: ValidationWarning[] = [];

      editedData.forEach(row => {
        if (conflicts.has(row.id) && !row._action) {
          const primaryKeyField = fields.find(f => f.required)?.key || "name";
          let conflictField = primaryKeyField;

          if (conflictFields.length > 0) {
            for (const field of conflictFields) {
              if (row[field]) {
                const existingRowByField = loadedData.find(
                  existing => existing[field] && existing[field] === row[field]
                );
                if (existingRowByField) {
                  conflictField = field;
                  break;
                }
              }
            }
          }

          conflictWarningsNew.push({
            rowId: row.id,
            field: conflictField,
            message: `Conflicts with existing data`,
            code: ValidationWarningCode.CONFLICT_WITH_EXISTING
          });
        }
      });

      const finalResult = {
        ...result,
        warnings: [...result.warnings, ...conflictWarningsNew]
      };

      setValidationResult(finalResult);
    } catch (error) {
      console.error("Validation error:", error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleCellChange = async (rowIndex: number, fieldKey: string, newValue: any) => {
    await onCellEdit(rowIndex, fieldKey, newValue);

    const updatedData = [...editedData];
    const currentRow = updatedData[rowIndex];
    updatedData[rowIndex] = {
      ...currentRow,
      [fieldKey]: newValue.entity ? newValue.entity.id : newValue,
      _modified: true
    };

    if (keptExistingRows.has(currentRow.id)) {
      setKeptExistingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentRow.id);
        return newSet;
      });
    }

    const primaryKeyField = fields.find(f => f.required)?.key || "name";

    let existingRow = loadedData.find(existing => existing[primaryKeyField] === updatedData[rowIndex][primaryKeyField]);

    if (!existingRow && conflictFields.length > 0) {
      for (const conflictField of conflictFields) {
        if (updatedData[rowIndex][conflictField]) {
          existingRow = loadedData.find(
            existing => existing[conflictField] && existing[conflictField] === updatedData[rowIndex][conflictField]
          );
          if (existingRow) break;
        }
      }
    }

    if (!existingRow) {
      setReplacedRows(prev => {
        const newMap = new Map(prev);
        newMap.delete(currentRow.id);
        return newMap;
      });

      delete updatedData[rowIndex]._action;
    }

    setEditedData(updatedData);
    identifyConflictsAndNewRows(updatedData);
  };

  const trackPotentialDeletions = () => {
    const primaryKeyField = fields.find(f => f.required)?.key || "name";
    const uploadedPrimaryKeys = new Set(editedData.map(row => row[primaryKeyField]).filter(key => key));

    const replacedExistingIds = new Set(replacedRows.values());

    const deletedIds = loadedData
      .filter(existing => {
        if (replacedExistingIds.has(existing.id)) {
          return false;
        }

        return !uploadedPrimaryKeys.has(existing[primaryKeyField]);
      })
      .map(existing => existing.id)
      .filter(id => id !== undefined);

    setDeletedExistingIds(new Set(deletedIds));
  };

  const handleDeleteRow = async (rowIndex: number) => {
    if (!window.confirm("Are you sure you want to delete this row?")) return;

    const rowToDelete = displayData[rowIndex];
    await onRowDelete(rowIndex);

    const updatedData = editedData.filter(row => row.id !== rowToDelete.id);
    setEditedData(updatedData);

    identifyConflictsAndNewRows(updatedData);
    handleValidation();
  };

  const handleAddNewRow = async () => {
    await onAddRow();

    const emptyRow: Record<string, any> = {};
    fields.forEach(field => {
      emptyRow[field.key] = null;
    });

    const maxId = Math.max(...editedData.map(row => row.id || 0), 0);
    emptyRow["id"] = maxId + 1;
    emptyRow["_isNew"] = true;

    const updatedData = [...editedData, emptyRow];
    setEditedData(updatedData);

    identifyConflictsAndNewRows(updatedData);
  };
  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedRows.length} selected row(s)?`;
    if (!window.confirm(confirmMessage)) return;

    const idsToDelete = selectedRows.map(index => displayData[index].id);

    const remainingData = editedData.filter(row => !idsToDelete.includes(row.id));
    setEditedData(remainingData);
    setSelectedRows([]);

    if (gridRef.current?.state?.gridApi) {
      gridRef.current.state.gridApi.deselectAll();
    }

    identifyConflictsAndNewRows(remainingData);
    handleValidation();
  };

  const handleSubmitClick = async () => {
    if (hasEmailValidationErrors) {
      const existingRowIds = new Set(editedData.map(row => row.id));
      const emailErrors =
        validationResult?.errors.filter(
          error =>
            error.rowId !== undefined &&
            existingRowIds.has(error.rowId) &&
            error.code === ValidationErrorCode.INVALID_EMAIL
        ) || [];

      alert(
        `Cannot submit: ${emailErrors.length} invalid email${
          emailErrors.length > 1 ? "s" : ""
        } found. Please fix all email validation errors before submitting.`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const dataToSubmit = editedData.filter(row => {
        if (keptExistingRows.has(row.id)) {
          return false;
        }
        return newRows.has(row.id) || row._action === "update" || row._modified === true;
      });

      const dataWithReplacementInfo = dataToSubmit.map(row => {
        const replacedId = replacedRows.get(row.id);
        if (replacedId !== undefined) {
          return { ...row, _replacesId: replacedId };
        }
        return row;
      });

      const submissionData = {
        rows: dataWithReplacementInfo,
        deletedIds: Array.from(deletedExistingIds)
      };

      await onSubmit(submissionData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getValidationStatus = () => {
    if (!validationResult) return { type: "info", message: "Validating..." };

    const existingRowIds = new Set(editedData.map(row => row.id));
    const validErrors = validationResult?.errors.filter(
      error => error.rowId !== undefined && existingRowIds.has(error.rowId)
    );
    const validWarnings = validationResult?.warnings.filter(
      warning => warning.rowId !== undefined && existingRowIds.has(warning.rowId)
    );

    const emailErrors = validErrors.filter(error => error.code === ValidationErrorCode.INVALID_EMAIL);

    if (validErrors.length > 0) {
      let message = `${validErrors.length} errors found`;
      if (emailErrors.length > 0) {
        message += ` (${emailErrors.length} invalid email${emailErrors.length > 1 ? "s" : ""})`;
      }
      return {
        type: "error",
        message
      };
    }

    if (validWarnings.length > 0) {
      return {
        type: "warning",
        message: `${validWarnings.length} warnings found`
      };
    }

    return { type: "success", message: "All data is valid" };
  };

  const getRowClass = (params: any) => {
    const rowId = params.data.id;
    if (keptExistingRows.has(rowId)) return "kept-existing-row";
    if (newRows.has(rowId)) return "new-row";
    return "";
  };

  const status = getValidationStatus();

  if (isLoadingData) {
    return <PositionedSpinner></PositionedSpinner>;
  }

  return (
    <div className="col p-0 m-0">
      {(conflictWarnings?.length > 0 || keptExistingRows.size > 0) && (
        <div className="conflict-resolution-bar row m-0 mb-3">
          <div className="col d-flex align-items-center justify-content-between">
            <span className="conflict-message">
              {keptExistingRows.size > 0
                ? `${keptExistingRows.size} row${
                    keptExistingRows.size > 1 ? "s" : ""
                  } will be skipped (keeping existing data)`
                : `${conflictWarnings?.length} row${
                    conflictWarnings?.length > 1 ? "s have" : " has"
                  } conflicts with existing data`}
            </span>
            <div>
              {keptExistingRows.size === 0 && (
                <>
                  <Button
                    type={ButtonTypes.OUTLINE_PRIMARY}
                    size={UiSizes.SM}
                    onClick={() => handleResolveAllConflicts("keep")}
                    className="mx-2"
                  >
                    Keep Existing
                  </Button>
                  <Button
                    type={ButtonTypes.OUTLINE_WARNING}
                    size={UiSizes.SM}
                    onClick={() => handleResolveAllConflicts("replace")}
                  >
                    Replace All
                  </Button>
                </>
              )}
              {keptExistingRows.size > 0 && (
                <Button
                  type={ButtonTypes.OUTLINE_SECONDARY}
                  size={UiSizes.SM}
                  onClick={() => {
                    setKeptExistingRows(new Set());
                    setConflictRows(new Set());
                    identifyConflictsAndNewRows(editedData);
                    handleValidation();
                  }}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="row d-flex flex-column justify-content-center align-items-start px-3 m-0">
        <h2>{I18n.t("phrases.validateData")}</h2>
        <p className="m-0">{I18n.t("phrases.reviewDataBeforeSubmit")}</p>
      </div>

      <div
        className={`validation-status row m-0 my-1 position-sticky ${
          conflictWarnings?.length > 0 || keptExistingRows.size > 0 ? "position-sticky--1" : "position-sticky--2"
        }`}
      >
        <div className={`col-2 status-badge status-${status.type} mr-2`}>{status.message}</div>
        {validationResult && (
          <div className="col-6 validation-stats">
            <div className="row w-100 d-flex justify-content-between">
              <span className="stat-item">Total rows: {displayData.length}</span>
              <span className="stat-item">Total errors: {editedDataErrorRows?.length}</span>
              <span className="stat-item">
                {I18n.t("phrases.mappedColumns")}: {Object.keys(columns).length}
              </span>
            </div>
          </div>
        )}
        <div className="summary-item col justify-content-end align-items-center">
          <div className="">
            <span className="legend-item uploader-cell-legend-item uploader-cell-legend-item--new">New</span>
            <span className="legend-item uploader-cell-legend-item uploader-cell-legend-item--replaced">Replaced</span>
            <span className="legend-item uploader-cell-legend-item uploader-cell-legend-item--error">Error</span>
            <span className="legend-item uploader-cell-legend-item uploader-cell-legend-item--warning">Warning</span>
          </div>
        </div>
      </div>

      <div
        className={`data-summary row m-0 mb-2 position-sticky ${
          conflictWarnings?.length > 0 || keptExistingRows.size > 0 ? "position-sticky--1" : "position-sticky--2"
        }`}
      >
        <div className="col d-flex flex-row justify-content-end align-items-center p-0">
          {editedDataErrorRows?.length > 0 && (
            <div className="col-4 p-1">
              <Button
                type={ButtonTypes.OUTLINE_DANGER}
                size={UiSizes.SM}
                onClick={handleDeleteErrorRows}
                className="m-1 w-100"
              >
                Delete All Error Rows ({editedDataErrorRows?.length})
              </Button>
            </div>
          )}
          {editedDataWithDuplicateRows?.length > 0 && (
            <div className="col-4 p-1">
              <Button
                type={ButtonTypes.OUTLINE_WARNING}
                size={UiSizes.SM}
                onClick={handleClearDuplicateRows}
                className="m-1 w-100"
              >
                Clear Duplicate Rows ({editedDataWithDuplicateRows?.length})
              </Button>
            </div>
          )}
        </div>
        {Object.entries(entityRelations).map(([entityType, entities]) => (
          <div key={entityType} className="summary-item col">
            <span className="summary-label">{entityType}:</span>
            <span className="summary-value">{entities.length} processed</span>
          </div>
        ))}
      </div>

      <div
        className={`data-grid-container insight-tab-content__space-filler position-sticky ${
          conflictWarnings?.length > 0 || keptExistingRows.size > 0 ? "position-sticky--1" : "position-sticky--2"
        }`}
      >
        <AppGrid
          ref={gridRef}
          context={{
            onDeleteRow: handleDeleteRow
          }}
          rowData={displayData}
          columnDefs={columnDefs}
          getRowClass={getRowClass}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            editable: true
          }}
          onCellEditModeChange={(cell, isEditing) => {}}
          onCellClicked={(cell, isEditMode) => {}}
          onRowSelected={event => {
            const selected = event.api.getSelectedNodes().map(node => node.rowIndex);
            setSelectedRows(selected);
          }}
          onCellValueChanged={params => {
            handleCellChange(params.node.rowIndex, params.colDef.field, params.newValue);
          }}
          onRowValueChanged={params => handleValidation()}
          shouldTrackSelectedCell={false}
          connectedUsers={[]}
          domLayout="normal"
          onGridReady={params => {
            params.api.sizeColumnsToFit();
          }}
        />
      </div>

      <div className="button-container row d-flex justify-content-between m-0 mt-4 px-3 py-3">
        <div>
          <Button type={ButtonTypes.OUTLINE_SECONDARY} onClick={onBack} isDisabled={isValidating || isSubmitting}>
            <span>{I18n.t("phrases.back")}</span>
          </Button>
        </div>

        <div className="d-flex align-items-center">
          <label className="mr-3">
            <input
              type="checkbox"
              checked={showErrorsOnly}
              onChange={e => setShowErrorsOnly(e.target.checked)}
              className="mr-2"
            />
            Show errors only
          </label>

          {selectedRows.length > 0 && (
            <Button
              className="mx-2"
              type={ButtonTypes.OUTLINE_DANGER}
              onClick={handleBulkDelete}
              isDisabled={isValidating || isSubmitting}
            >
              <span>Delete Selected ({selectedRows.length})</span>
            </Button>
          )}

          <Button
            className="mx-2"
            type={ButtonTypes.OUTLINE_DARK}
            onClick={handleAddNewRow}
            isDisabled={isValidating || isSubmitting}
          >
            <span>{I18n.t("phrases.addRow")}</span>
          </Button>

          <Button type={ButtonTypes.SECONDARY} onClick={handleValidation} isDisabled={isValidating || isSubmitting}>
            <span>{I18n.t("phrases.revalidate")}</span>
          </Button>

          <Button
            className="ml-2"
            type={ButtonTypes.PRIMARY}
            onClick={handleSubmitClick}
            isDisabled={
              isValidating ||
              isSubmitting ||
              !validationResult ||
              validationResult?.errors.length > 0 ||
              (validationResult?.warnings.length > 0 && keptExistingRows.size === 0 && replacedRows.size === 0) ||
              hasEmailValidationErrors
            }
          >
            <span>{isSubmitting ? I18n.t("phrases.submitting") : I18n.t("phrases.submit")}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
